import { DevelopmentReference } from "modules/development/models/Index";
import {
  TFirestoreTimestamp,
  TQueryDocumentSnapshot,
} from "common/types/FirebaseTypes";
import { now } from "utils/FirebaseUtils";
import { convertByteToKb } from "utils/MathUtils";
import { FirebaseDocument } from "common/models/FirebaseDocument";

/**
 * Modelo de arquivo 3D.
 * @category Files
 * @interface Library
 * @hideconstructor
 */
export class Library {
  /** Nome do arquivo salvo no Storage. */
  name: string;
  /** Status da conversão das propriedades do modelo 3D. */
  status: string;
  /** Data de criação do documento do arquivo. */
  createdAt: TFirestoreTimestamp;
  /** Tipo do arquivo que pode ser ifc ou ifczip (não utilizado mais). */
  type: string;
  /** Tamanho do arquivo em KB. */
  fileSize: number;
  /** Progresso da conversão das propriedades do modelo 3D. */
  progress?: string;
  /** Projeto em que o arquivo pertence. */
  development?: DevelopmentReference;
  /** Flag que indica se o modelo 3D será exibido no Visualizador 3D. */
  enabled?: boolean;
  /** Mensagem de erro da importação */
  erro?: string;
  /** Data de modificação do documento do arquivo. */
  modifyAt?: TFirestoreTimestamp;
  /** Status da conversão do modelo 3D para formato lido pelo visualizador. */
  statusXkt?: string;
  /** Nome do arquivo que será exibido no Front. */
  realFileName: string;
  /** Progresso da conversão do modelo 3D. */
  progressXkt?: string;
  /** Tempo em que foi finalizada a conversão do modelo 3D. */
  finishTimeOfConvertion?: boolean;
  /** Tempo em que foi finalizada a conversão das propriedades do modelo 3D. */
  finishTimeOfReadProperties?: boolean;
  /** Tempo total de conversão do modelo 3D. */
  timeOfConvertion?: string;
  /** Tempo total de conversão das propriedades do modelo 3D. */
  timeOfReadProperties?: string;
  /** Data do upload do arquivo. */
  uploadDate?: number;

  constructor(
    name: string,
    status: string,
    createdAt: TFirestoreTimestamp,
    type: string,
    realFileName: string,
    fileSize: number = 0,
    development?: DevelopmentReference,
    enabled?: boolean,
    erro?: string,
    modifyAt?: TFirestoreTimestamp,
    progress?: string,
    statusXkt?: string,
    progressXkt?: string,
    finishTimeOfConvertion?: boolean,
    finishTimeOfReadProperties?: boolean,
    timeOfConvertion?: string,
    timeOfReadProperties?: string,
    uploadDate?: number
  ) {
    this.name = name;
    this.status = status;
    this.createdAt = createdAt;
    this.type = type;
    this.fileSize = fileSize;
    this.progress = progress;
    this.development = development;
    this.enabled = enabled;
    this.erro = erro;
    this.modifyAt = modifyAt;
    this.statusXkt = statusXkt;
    this.realFileName = realFileName;
    this.progressXkt = progressXkt;
    this.finishTimeOfConvertion = finishTimeOfConvertion;
    this.finishTimeOfReadProperties = finishTimeOfReadProperties;
    this.timeOfConvertion = timeOfConvertion;
    this.timeOfReadProperties = timeOfReadProperties;
    this.uploadDate = uploadDate;
  }

  /** Flag que indica se a importação está em andamento. */
  get isImportProcessing() {
    if (this.status == "processed" && this.statusXkt == "processed")
      return false;
    return true;
  }

  /** Flag que indica se a importação foi finalizada. */
  get isImportFinished() {
    if (!this.statusXkt) return this.status === "processed";
    return this.status === "processed" && this.statusXkt === "processed";
  }

  /** Progresso da importação do modelo juntando os progressos das conversões. */
  get importProgress() {
    let properties =
      this.status === "processed" ? 100 : parseInt(this.progress || "0");
    let xkt =
      this.statusXkt === "processed" ? 100 : parseInt(this.progressXkt || "0");

    const progress = (properties + xkt) / 2;
    return Math.round(progress);
  }
}

export function createLibraryModelFromDocument(
  doc: TQueryDocumentSnapshot
): FirebaseDocument<Library> {
  const data = doc.data();
  if (!data) throw new Error("Invalid library data!");

  const library = new Library(
    data.name,
    data.status,
    data.createdAt,
    data.type,
    data.realFileName,
    data.fileSize,
    data.development,
    data.enabled,
    data.erro,
    data.modifyAt,
    data.progress,
    data.statusXkt,
    data.progressXkt
  );
  return new FirebaseDocument<Library>(doc.id, library);
}

export function createNewLibrary(
  file: File,
  development: DevelopmentReference
) {
  const realFileName = development.id + file.name;
  const fileName = development.id + formatName(file.name) + ".ifc";
  const type = extractExtension(fileName);

  return new Library(
    fileName,
    "pending",
    now(),
    type,
    realFileName,
    convertByteToKb(file.size),
    development,
    false,
    "",
    now(),
    "",
    "pending",
    "",
    false,
    false,
    "",
    "",
    Date.now()
  );
}

function formatName(fileName: string): string {
  const formattedFileName = fileName.replace(/[^a-zA-Z0-9]/g, "");
  return formattedFileName.toLocaleLowerCase();
}

function extractExtension(filename: string): string {
  const ext = filename.split(".").pop() || "";
  return ext.toLowerCase();
}



// WEBPACK FOOTER //
// ./src/modules/library/models/Library.ts